import {isPlatformBrowser} from '@angular/common';
import {Inject, Injectable, OnDestroy, PLATFORM_ID} from '@angular/core';
import {CookieService} from 'ngx-cookie-service';
import {Subscription} from 'rxjs';
import {DeviceTypeEnum} from '../enums/DeviceTypeEnum';
import {GtmEventEnum} from '../enums/GoogleEnums';
import {AppConfigService} from './app-config.service';
import {CodeSnippetsService} from './code-snippets.service';
import {ToolsService} from './tools.service';

@Injectable({
  providedIn: 'root',
})
export class GtmService implements OnDestroy {
  private dataLayerQueue: any[] = [];
  private isThisFirstImpression: boolean = true;
  private subscriptions: Subscription[] = [];

  constructor(
    private additionalScripts: CodeSnippetsService,
    private tools: ToolsService,
    private cookieService: CookieService,
    private appConfigService: AppConfigService,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {}

  public ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public pushTag(tag: any, skipDeviceId?: boolean): void {
    if (skipDeviceId !== true) {
      tag.VardenDeviceID = this.getDecodedVardenDeviceIdFromCookie();
    }

    if (this.isThisFirstImpression === true) {
      switch (tag.event) {
        case GtmEventEnum.VIRTUAL_PAGE_VIEW:
          this.convertVirtualPageViewToInitialTagAndPushAllTagsAndScripts(tag);
          break;
        default:
          this.dataLayerQueue.push(tag);
      }
    } else {
      this.pushTagToDataLayer(tag);
    }
  }

  private convertVirtualPageViewToInitialTagAndPushAllTagsAndScripts(tag: any): void {
    if (isPlatformBrowser(this.platformId)) {
      delete tag.event;
      this.pushConfigCommandsToDataLayer();
      this.pushTagToDataLayer(tag);
      this.pushQueuedTags();
      this.additionalScripts.addScriptsToHead();
      this.additionalScripts.addScriptsToBody();
    }
    this.isThisFirstImpression = false;
  }

  private pushQueuedTags(): void {
    this.dataLayerQueue.forEach((tag) => this.pushTagToDataLayer(tag));
  }

  private pushTagToDataLayer(tag: any): void {
    if (
      isPlatformBrowser(this.platformId) &&
      ((!this.tools.isNil((window as any).Cypress) && this.tools.getDeviceType() === DeviceTypeEnum.HEADLESS) ===
        false ||
        this.appConfigService.appConfig.features.enableGtmForCypress === true)
    ) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(tag);
    }
  }

  private getDecodedVardenDeviceIdFromCookie(): string {
    const vardenDeviceId = this.cookieService.get('tid');
    return this.tools.isNil(vardenDeviceId) ? null : atob(vardenDeviceId);
  }

  private pushConfigCommandsToDataLayer(): void {
    this.pushCommandToDataLayer([
      'consent',
      'default',
      {
        ad_storage: 'denied',
        analytics_storage: 'denied',
        functionality_storage: 'denied',
        personalization_storage: 'denied',
        security_storage: 'granted',
        wait_for_update: 500,
      },
    ]);

    this.pushCommandToDataLayer(['set', 'ads_data_redaction', true]);
  }

  private pushCommandToDataLayer(command: any[]): void {
    if (
      isPlatformBrowser(this.platformId) &&
      ((!this.tools.isNil((window as any).Cypress) && this.tools.getDeviceType() === DeviceTypeEnum.HEADLESS) ===
        false ||
        this.appConfigService.appConfig.features.enableGtmForCypress === true)
    ) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(command);
    }
  }
}
